import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { adminClient } from '../data/adminClient';
import RichTextEditor from './RichTextEditor';

// The contract every leader receives, written once with {placeholders} that
// are filled in per leader (name, workshops, sessions, pay, event). Saved for
// this event; next year starts from this wording.
export default function ContractTemplateDialog({ open, onClose, leaders }) {
  const [tpl, setTpl] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [previewFor, setPreviewFor] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTpl(null); setError(null); setSaved(false);
    setPreviewFor(leaders[0]?.engagementId || '');
    adminClient.getContractTemplate()
      .then(t => { setTpl(t); setTitle(t.title); setBody(t.body); })
      .catch(err => setError(err.message));
    // leaders is a fresh array each render; only reload when (re)opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changed = tpl && (title !== tpl.title || body !== tpl.body);

  // Inserts at the cursor in the editor (or the end, if it hasn't been clicked).
  const insertPlaceholder = (name) => editorRef.current?.insertText(`{${name}}`);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const t = await adminClient.saveContractTemplate({ title, body });
      setTpl(t); setTitle(t.title); setBody(t.body); setSaved(true);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // The PDF is built from the saved template, so save first if there are edits.
  const preview = async () => {
    if (changed && !(await save())) return;
    adminClient.openLeaderContractPdf(previewFor);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contract template</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Write the contract once — the parts in {'{braces}'} are filled in for each leader. Send it from the <strong>Email</strong> button
          (“Send contract”): it attaches their PDF and opens it for signing on their private link, where they can sign online or upload a signed copy.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!tpl ? (
          !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <TextField size="small" fullWidth label="Title" value={title} onChange={e => { setTitle(e.target.value); setSaved(false); }} inputProps={{ maxLength: 120 }} />
            <Box sx={{ mt: 2 }}>
              <RichTextEditor ref={editorRef} initialValue={tpl.body} onChange={(html) => { setBody(html); setSaved(false); }} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>Click to insert:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {tpl.placeholders.map(p => <Chip key={p} size="small" label={`{${p}}`} onClick={() => insertPlaceholder(p)} />)}
            </Box>
            {leaders.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Preview as</InputLabel>
                  <Select label="Preview as" value={previewFor} onChange={e => setPreviewFor(e.target.value)}>
                    {leaders.map(l => <MenuItem key={l.engagementId} value={l.engagementId}>{l.profile.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button startIcon={<PictureAsPdfIcon />} onClick={preview} disabled={!previewFor || saving}>
                  {changed ? 'Save & preview PDF' : 'Preview PDF'}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {saved && !changed && <Typography variant="body2" color="success.main" sx={{ mr: 'auto', pl: 2 }}>Saved ✓</Typography>}
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button variant="contained" onClick={save} disabled={!tpl || saving || !changed || !body.trim()}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
